import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { Pencil, Plus, Trash2, Building2, AlertTriangle, Eye, FileText, LogIn } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { logCrud } from "@/services/activities";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseISO, isAfter, format as formatDateFns } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TenantDetailsModal } from "@/components/admin/TenantDetailsModal";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import TenantDetails from "@/pages/TenantDetails";
import { startOfDay, endOfDay, subDays } from "date-fns";
import { useRef } from "react";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  status: string;
  trial_ends_at: string | null;
  created_at: string;
  usage: {
    users: number;
  } | null;
  subscription_status: string | null;
  subscription_plan_id: string | null;
  subscription_type: string | null;
  subscription_payment_method: string | null;
  subscription_start_date: string | null;
  subscription_next_billing_date: string | null;
  subscription_last_payment_date: string | null;
  colors: any;
  images: any;
}

const Tenants = () => {
  const navigate = useNavigate();
  const { startImpersonation, isAdminSaas } = useTenant();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [firstConfirmDialogOpen, setFirstConfirmDialogOpen] = useState(false);
  const [secondConfirmDialogOpen, setSecondConfirmDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [tenantNameInput, setTenantNameInput] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);
  const [tenantProfile, setTenantProfile] = useState<{ id: string; name: string } | null>(null);
  
  const queryClient = useQueryClient();

  // Buscar todos os planos disponíveis para obter nomes
  const { data: availablePlans } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("id, name")
        .eq("status", "active");
      
      if (error) throw error;
      return data || [];
    },
  });

  // Função para obter o nome do plano pelo ID
  const getPlanName = (planId: string | null) => {
    if (!planId) return "Trial";
    const plan = availablePlans?.find(p => p.id === planId);
    return plan?.name || planId;
  };

  // Buscar todas as transações ativas de todos os tenants
  const { data: allTransactions } = useQuery({
    queryKey: ["all-subscription-transactions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("id, tenant_id, status, expiration_date, payment_date")
        .eq("status", "approved")
        .order("expiration_date", { ascending: false, nullsFirst: false });
      
      if (error) throw error;
      return data || [];
    },
  });

  const { data: tenants, isLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select(`
          *,
          usage,
          subscription_status,
          subscription_plan_id,
          subscription_type,
          subscription_payment_method,
          subscription_start_date,
          subscription_next_billing_date,
          subscription_last_payment_date,
          colors,
          images
        `)
        .order("created_at", { ascending: false }) as any;
      
      if (error) {
        console.error("Erro ao buscar tenants:", error);
        throw error;
      }
      return (data || []) as Tenant[];
    },
  });

  const uniqueTenants = useMemo(() => {
    const map = new Map<string, Tenant>();
    const score = (x: Tenant) => {
      let s = 0;
      if (x.subscription_status === "active") s += 2;
      if (x.subscription_plan_id) s += 1;
      return s;
    };
    (tenants || []).forEach((t) => {
      const key = (t.email || "").toLowerCase() || t.id;
      if (!map.has(key)) {
        map.set(key, t);
      } else {
        const current = map.get(key)!;
        map.set(key, score(t) > score(current) ? t : current);
      }
    });
    return Array.from(map.values());
  }, [tenants]);

  const deleteMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data, error } = await supabase.functions.invoke('delete-tenant', {
        body: { tenantId }
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success("Cliente e todos os dados relacionados foram excluídos permanentemente!");
      if (tenantToDelete?.id) {
        void logCrud({ entitySlug: "entity:tenants", operationSlug: "delete", entityId: tenantToDelete.id, entityName: tenantToDelete.name, screenSlug: "clientes-saas" });
      }
      setSecondConfirmDialogOpen(false);
      setFirstConfirmDialogOpen(false);
      setTenantToDelete(null);
      setTenantNameInput("");
    },
    onError: (error: any) => {
      toast.error("Erro ao excluir cliente: " + error.message);
      setSecondConfirmDialogOpen(false);
      setFirstConfirmDialogOpen(false);
      setTenantToDelete(null);
      setTenantNameInput("");
    },
  });

  const handleDelete = (tenant: Tenant) => {
    setTenantToDelete(tenant);
    setTenantNameInput("");
    setFirstConfirmDialogOpen(true);
  };

  const handleFirstConfirm = () => {
    if (tenantToDelete && tenantNameInput === tenantToDelete.name) {
      setFirstConfirmDialogOpen(false);
      setSecondConfirmDialogOpen(true);
    } else {
      toast.error("O nome do cliente não corresponde. Digite exatamente como está na base de dados.");
    }
  };

  const handleFinalConfirm = () => {
    if (tenantToDelete) {
      deleteMutation.mutate(tenantToDelete.id);
    }
  };

  // Função para obter label e cor do status em português
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      trial: "Trial",
      active: "Ativo",
      suspended: "Suspenso",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  };

  // Função para traduzir o tipo de assinatura para português
  const getSubscriptionTypeLabel = (type: string | null) => {
    if (!type) return "Regular";
    const labels: Record<string, string> = {
      regular: "Regular",
      cortesia: "Cortesia",
      testers: "Beta Tester",
      promotion: "Promoção",
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string) => {
    // Retorna variant e className para cores personalizadas
    if (status === "active") {
      return { variant: "default" as const, className: "bg-green-600 hover:bg-green-700 text-white" };
    }
    if (status === "trial") {
      return { variant: "secondary" as const, className: "bg-gray-500 hover:bg-gray-600 text-white" };
    }
    if (status === "suspended") {
      return { variant: "destructive" as const, className: "bg-red-600 hover:bg-red-700 text-white" };
    }
    if (status === "cancelled") {
      return { variant: "outline" as const, className: "bg-gray-400 hover:bg-gray-500 text-white" };
    }
    return { variant: "outline" as const, className: "" };
  };

  const getPlanBadge = (plan: string | null) => {
    if (plan === "plus") return "default";
    if (plan === "plus_whats") return "default";
    if (plan === "plus_whats_ia") return "default";
    return "secondary";
  };

  const handleViewDetails = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setDetailsModalOpen(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  // Função para calcular a data até quando o tenant está ativo
  const getActiveUntilDate = (tenant: Tenant) => {
    const now = new Date();
    
    // Buscar transações ativas deste tenant
    const tenantTransactions = allTransactions?.filter(t => t.tenant_id === tenant.id) || [];
    
    // Verificar se há transação lifetime
    const lifetimeTransaction = tenantTransactions.find(
      t => t.expiration_date === null && t.status === 'approved'
    );
    
    if (lifetimeTransaction) {
      return null; // Lifetime - não expira
    }
    
    // Buscar transação com expiration_date mais distante (não expirada)
    const activeTransactionsWithExpiration = tenantTransactions
      .filter(t => {
        if (!t.expiration_date) return false;
        const expiration = parseISO(t.expiration_date);
        return isAfter(expiration, now);
      })
      .sort((a, b) => {
        const dateA = parseISO(a.expiration_date!);
        const dateB = parseISO(b.expiration_date!);
        return dateB.getTime() - dateA.getTime();
      });
    
    if (activeTransactionsWithExpiration.length > 0) {
      return parseISO(activeTransactionsWithExpiration[0].expiration_date!);
    }
    
    // Se não tem transações ativas, verificar subscription_next_billing_date
    if (tenant.subscription_next_billing_date) {
      const nextBilling = parseISO(tenant.subscription_next_billing_date);
      if (isAfter(nextBilling, now)) {
        return nextBilling;
      }
    }
    
    // Fallback: verificar trial_ends_at
    if (tenant.trial_ends_at) {
      const trialEnds = parseISO(tenant.trial_ends_at);
      if (isAfter(trialEnds, now)) {
        return trialEnds;
      }
    }
    
    // Se não há nenhuma data futura, retornar null (já expirado)
    return null;
  };

  // Função para obter a cor de fundo da linha baseada no tipo/status
  const getRowBackgroundColor = (tenant: Tenant) => {
    // Se é cortesia
    if (tenant.subscription_type === 'cortesia') {
      return 'bg-amber-50 hover:bg-amber-100';
    }
    // Se é tester
    if (tenant.subscription_type === 'testers') {
      return 'bg-blue-50 hover:bg-blue-100';
    }
    // Se é promoção
    if (tenant.subscription_type === 'promotion') {
      return 'bg-purple-50 hover:bg-purple-100';
    }
    // Se é regular ativo
    if (tenant.subscription_type === 'regular' && tenant.subscription_status === 'active') {
      return 'bg-green-50 hover:bg-green-100';
    }
    // Se é trial
    if (tenant.subscription_status === 'trial') {
      return 'bg-pink-50 hover:bg-pink-100';
    }
    // Cor padrão (branco)
    return 'hover:bg-muted/50';
  };

  // Função para formatar a data de ativo até
  const formatActiveUntil = (tenant: Tenant) => {
    const activeUntil = getActiveUntilDate(tenant);
    
    if (activeUntil === null) {
      // Verificar se é lifetime ou expirado
      const tenantTransactions = allTransactions?.filter(t => t.tenant_id === tenant.id) || [];
      const hasLifetime = tenantTransactions.some(
        t => t.expiration_date === null && t.status === 'approved'
      );
      
      if (hasLifetime) {
        return "Para sempre";
      }
      
      // Verificar se está expirado
      if (tenant.subscription_status === 'active' || tenant.subscription_status === 'trial') {
        return "-";
      }
      
      return "Expirado";
    }
    
    return formatDateFns(activeUntil, "dd/MM/yyyy");
  };

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTenants = uniqueTenants.slice(startIndex, endIndex);
  const totalPages = Math.max(1, Math.ceil(uniqueTenants.length / itemsPerPage));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-8 w-8" />
            Clientes SaaS do Caixa Fácil
          </h1>
          <p className="text-muted-foreground">
            Gerencie todos os clientes do sistema
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Listagem de Clientes SaaS do Caixa Fácil</CardTitle>
          <CardDescription>
            {uniqueTenants.length} {uniqueTenants.length === 1 ? "cliente" : "clientes"} cadastrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome / Email</TableHead>
                      <TableHead className="text-center">Plano</TableHead>
                      <TableHead className="text-center">Tipo</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead>Ativo até</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTenants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          Nenhum cliente encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedTenants.map((tenant) => {
                        const statusBadge = getStatusBadge(tenant.status);
                        const planId = tenant.subscription_plan_id || "trial";
                        const planName = getPlanName(tenant.subscription_plan_id);
                        const statusLabel = getStatusLabel(tenant.status);
                        const rowBgColor = getRowBackgroundColor(tenant);
                        
                        return (
                          <TableRow key={tenant.id} className={rowBgColor}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">{tenant.name}</div>
                              {tenant.email && (
                                <div className="text-xs text-muted-foreground">{tenant.email}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={getPlanBadge(planId)}>
                              {planName}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">
                              {getSubscriptionTypeLabel(tenant.subscription_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={statusBadge.variant} className={statusBadge.className}>
                              {statusLabel}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatActiveUntil(tenant)}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              {isAdminSaas && (
                                <Button
                                  variant="default"
                                  size="icon"
                                  className="icon-chip-orange"
                                  onClick={async () => {
                                    await startImpersonation(tenant.id);
                                    navigate('/dashboard');
                                  }}
                                  title="Entrar como este cliente"
                                >
                                  <LogIn className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="icon-chip-blue"
                                onClick={() => setTenantProfile({ id: tenant.id, name: tenant.name })}
                                title="Ver detalhes completos"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="icon-chip-green"
                                onClick={() => handleViewDetails(tenant)}
                                title="Visualizar detalhes (modal)"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="icon"
                                className="icon-chip-red"
                                onClick={() => handleDelete(tenant)}
                                title="Excluir cliente"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {uniqueTenants.length > 0 && (
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={uniqueTenants.length}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Primeiro diálogo: Confirmação com digitação do nome */}
      <AlertDialog open={firstConfirmDialogOpen} onOpenChange={setFirstConfirmDialogOpen}>
        <AlertDialogContent className="max-w-2xl border-destructive border-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-2xl text-destructive">
              <AlertTriangle className="h-8 w-8" />
              ATENÇÃO: EXCLUSÃO PERMANENTE DE CLIENTE
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4 text-base">
              <div className="bg-destructive/10 p-4 rounded-lg border border-destructive">
                <p className="font-semibold text-foreground mb-2">
                  Você está prestes a excluir permanentemente o cliente:
                </p>
                <p className="text-xl font-bold text-destructive mb-2">
                  {tenantToDelete?.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  ID: {tenantToDelete?.id}
                </p>
              </div>
              
              <div className="space-y-2">
                <p className="font-semibold text-foreground">
                  Esta ação irá excluir PERMANENTEMENTE:
                </p>
                <ul className="list-disc list-inside space-y-1 text-foreground text-sm">
                  <li>Usuários e Perfis (perfis serão desassociados do cliente)</li>
                  <li>Produtos e Categorias de Produtos</li>
                  <li>Clientes e Fornecedores</li>
                  <li>Categorias de Despesas</li>
                  <li>Transações, Itens de Transações e Pagamentos</li>
                  <li>Sessões de Caixa e Breakdown</li>
                  <li>Contas Bancárias e Métodos de Pagamento</li>
                  <li>Configurações de Recebimento</li>
                  <li>Formulários, Campos, Submissões e Valores</li>
                  <li>Dados de IA (requisições, índices, eventos de uso)</li>
                  <li>Transações de Assinatura</li>
                  <li>Eventos de Sincronização do Chatwoot</li>
                  <li>Todos os dados relacionados sem possibilidade de recuperação</li>
                </ul>
              </div>

              <div className="space-y-2 bg-muted p-4 rounded-lg">
                <Label htmlFor="tenant-name" className="text-base font-semibold">
                  Para confirmar, digite o nome EXATO do cliente:
                </Label>
                <Input
                  id="tenant-name"
                  value={tenantNameInput}
                  onChange={(e) => setTenantNameInput(e.target.value)}
                  placeholder={tenantToDelete?.name}
                  className="font-mono text-lg"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Atenção: Letras maiúsculas e minúsculas devem corresponder exatamente
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setTenantNameInput("");
              setTenantToDelete(null);
            }}>
              Cancelar
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleFirstConfirm}
              disabled={tenantNameInput !== tenantToDelete?.name}
            >
              Prosseguir para Confirmação Final
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Segundo diálogo: Confirmação final */}
      <AlertDialog open={secondConfirmDialogOpen} onOpenChange={setSecondConfirmDialogOpen}>
        <AlertDialogContent className="max-w-5xl border-destructive border-4 bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-3xl text-destructive animate-pulse">
              <AlertTriangle className="h-10 w-10" />
              ÚLTIMA CONFIRMAÇÃO
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-6 text-base">
              <div className="bg-destructive text-destructive-foreground p-6 rounded-lg space-y-4">
                <p className="text-2xl font-bold text-center">
                  ⚠️ ESTA AÇÃO É IRREVERSÍVEL ⚠️
                </p>
                <p className="text-lg text-center">
                  Você confirma a exclusão PERMANENTE do cliente:
                </p>
                <p className="text-2xl font-bold text-center">
                  {tenantToDelete?.name}
                </p>
              </div>

              <div className="space-y-3 text-foreground">
                <p className="text-lg font-semibold text-center">
                  Todos os seguintes dados serão perdidos PARA SEMPRE:
                </p>
                <div className="grid grid-cols-4 gap-2 text-sm max-h-[400px] overflow-y-auto pr-2">
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Usuários e Perfis
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Produtos
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Categorias de Produtos
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Clientes
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Fornecedores
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Categorias de Despesas
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Transações
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Itens de Transações
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Pagamentos das Transações
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Sessões de Caixa
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Breakdown de Sessões
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Contas Bancárias
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Métodos das Contas
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Configurações de Recebimento
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Formulários
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Campos de Formulários
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Submissões de Formulários
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Valores das Submissões
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Opções de Campos
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Dependências de Campos
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Modificadores de Preço
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Requisições de IA
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Assets de IA
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Índice de Busca de IA
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Eventos de Uso de IA
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Transações de Assinatura
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-destructive text-foreground">
                    ✗ Eventos Chatwoot
                  </div>
                </div>
              </div>

              <div className="bg-red-50 p-4 rounded-lg border-2 border-destructive">
                <p className="text-center font-bold text-destructive text-lg">
                  NÃO HÁ COMO DESFAZER ESTA OPERAÇÃO
                </p>
                <p className="text-center text-muted-foreground text-sm mt-2">
                  Todos os dados serão apagados permanentemente do banco de dados
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              className="w-full sm:w-auto"
              onClick={() => {
                setTenantNameInput("");
                setTenantToDelete(null);
              }}
            >
              Cancelar - Não Excluir
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleFinalConfirm}
              disabled={deleteMutation.isPending}
              className="w-full sm:w-auto bg-destructive hover:bg-destructive/90 text-lg py-6"
            >
              {deleteMutation.isPending ? "Excluindo..." : "CONFIRMAR EXCLUSÃO PERMANENTE"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedTenant && (
        <TenantDetailsModal
          open={detailsModalOpen}
          onOpenChange={(open) => {
            setDetailsModalOpen(open);
            if (!open) {
              queryClient.invalidateQueries({ queryKey: ["tenants"] });
            }
          }}
          tenant={selectedTenant}
        />
      )}

      {(typeof tenantProfile !== "undefined") && tenantProfile && (
        <RaioXModal
          open={!!tenantProfile}
          onOpenChange={(open) => { if (!open) setTenantProfile(null); }}
          entityTypeLabel={"CLIENTE SAAS"}
          entityName={tenantProfile.name}
          infoItems={[]}
          showPeriodControls
          initialFrom={startOfDay(subDays(new Date(), 6))}
          initialTo={endOfDay(new Date())}
          contentClassName="p-4"
          renderContent={({ periodRange, setLoading }) => (
            <TenantDetails id={tenantProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
          )}
        />
      )}
    </div>
  );
};

export default Tenants;
import { useTenant } from "@/contexts/TenantContext";
